import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom";

import ProviderTaggingRoute from "../routes/ProviderTaggingRoute";
import SupervisionRoute from "../routes/SupervisionRoute";
import PrivateRoute from "./PrivateRoute";
import ProfilePreviewRoute from "./ProfilePreviewRoute";
import QueryProviderRoute from "./QueryProviderRoute";
import RootRoute from "./RootRoute";

// screen imports
import ForgotPassword from "../components/Auth/ForgotPassword";
import Login from "../components/Auth/Login";
import RouteErrorFallback from "../RouteErrorFallback";
import PageNotFound from "./PageNotFound";

const lazyLoader = importer => () =>
  importer().then(module => ({
    element: <module.default />,
  }));

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<QueryProviderRoute />}>
      <Route element={<RootRoute />} errorElement={<RouteErrorFallback />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        <Route path="/login" element={<Login />} />

        <Route path="/forgotpassword" element={<ForgotPassword />} />

        <Route
          path="/ehr/response"
          lazy={lazyLoader(
            () =>
              import("../components/Clients/ClientProfile/AllNotes/Ehr/ViewReport")
          )}
        />

        <Route
          path="/risk-assessment/response"
          lazy={lazyLoader(
            () =>
              import("../components/Clients/ClientProfile/AllNotes/ViewRiskAssessment")
          )}
        />

        <Route
          path="/approve-session/:id"
          lazy={lazyLoader(() => import("../components/SessionApproval"))}
        />

        <Route
          path="/deny-session/:id"
          lazy={lazyLoader(() => import("../components/SessionApproval"))}
        />

        <Route element={<PrivateRoute />}>
          <Route
            path="/dashboard"
            lazy={lazyLoader(() => import("../components/Dashboard"))}
          />

          <Route
            path="/dashboard/sessions"
            lazy={lazyLoader(() => import("../components/Sessions"))}
          />

          <Route
            path="/dashboard/:uuid/booking/:bookingId"
            lazy={lazyLoader(() => import("../components/Dashboard"))}
          />

          <Route
            path="/notifications"
            lazy={lazyLoader(() => import("../components/NotificationsUpdate"))}
          />

          <Route
            path="/notif"
            lazy={lazyLoader(
              () => import("../components/AppNotificationRouting")
            )}
          />

          <Route
            path="/analytics"
            lazy={lazyLoader(() => import("../components/Analytics"))}
          />

          <Route
            path="/analytics/query/create"
            lazy={lazyLoader(
              () => import("../components/Analytics/CreateEditQuery")
            )}
          />

          <Route
            path="/analytics/query/edit/:id"
            lazy={lazyLoader(
              () => import("../components/Analytics/CreateEditQuery")
            )}
          />

          <Route
            path="/analytics/queries"
            lazy={lazyLoader(
              () =>
                import("../components/Analytics/SearchClientToEngage/QueryList")
            )}
          />

          <Route
            path="/analytics/clients"
            lazy={lazyLoader(
              () => import("../components/Analytics/ClientsList")
            )}
          />

          <Route
            path="/analytics/batch-actions-history"
            lazy={lazyLoader(
              () => import("../components/Analytics/BatchActionsHistory")
            )}
          />

          <Route
            path="/availability"
            lazy={lazyLoader(
              () => import("../components/ProviderProfile/Availability")
            )}
          />

          <Route
            path="/transactions"
            lazy={lazyLoader(() => import("../components/Transactions"))}
          />

          <Route
            path="/canned-messages"
            lazy={lazyLoader(() => import("../components/CannedMessages"))}
          />

          <Route
            path="/help-and-updates"
            lazy={lazyLoader(() => import("../components/HelpAndUpdates"))}
          />

          <Route
            path="/coach-call"
            lazy={lazyLoader(() => import("../components/CoachCall"))}
          />

          <Route
            path="/ai-home"
            lazy={lazyLoader(() => import("../components/AIHome"))}
          />

          <Route
            path="/ai-home/faqs"
            lazy={lazyLoader(
              () => import("../components/AIHome/FAQListingPage")
            )}
          />
        </Route>

        <Route lazy={lazyLoader(() => import("../routes/ClientsRoute"))}>
          <Route
            path="/clients/:client"
            lazy={lazyLoader(
              () => import("../components/Clients/ClientProfile")
            )}
          />

          <Route
            path="/clients"
            lazy={lazyLoader(
              () => import("../components/Clients/ClientProfile")
            )}
          />

          <Route
            path="/supervision-mode/clients/:client"
            lazy={lazyLoader(
              () => import("../components/Clients/ClientProfile")
            )}
          />

          <Route
            path="/supervision-mode/clients"
            lazy={lazyLoader(
              () => import("../components/Clients/ClientProfile")
            )}
          />
        </Route>

        <Route element={<ProfilePreviewRoute />}>
          <Route
            path="/profile/preview"
            lazy={lazyLoader(() => import("../components/ProfilePreview"))}
          />
        </Route>

        <Route lazy={lazyLoader(() => import("../routes/ProfileRoute"))}>
          <Route
            path="/profile"
            lazy={lazyLoader(() => import("../components/ProviderProfile"))}
          />
        </Route>

        <Route element={<ProviderTaggingRoute />}>
          <Route
            path="/provider-tagging/update-history"
            lazy={lazyLoader(
              () => import("../components/ProviderTagging/UpdateHistory")
            )}
          />

          <Route
            path="/provider-tagging/create-new"
            lazy={lazyLoader(
              () =>
                import("../components/ProviderTagging/CreateProviderTaggingEntry")
            )}
          />
        </Route>

        <Route lazy={lazyLoader(() => import("../routes/GroupsRoute"))}>
          <Route
            path="/groups/:group"
            lazy={lazyLoader(() => import("../components/Groups/GroupDetails"))}
          />

          <Route
            path="/groups"
            lazy={lazyLoader(() => import("../components/Groups/GroupDetails"))}
          />
        </Route>

        <Route element={<SupervisionRoute />}>
          <Route
            path="/supervision"
            lazy={lazyLoader(() => import("../components/Supervision"))}
          />
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Route>
  )
);
